import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import emailjs from "emailjs-com";
import Navbar from "./Navbar";
import Footer from "./Footer";
import PageHeader from "./PageHeader";
import { useSelector } from "react-redux";
import "../css/Needhelp.css";

// ─── Cloudinary Config ────────────────────────────────────────────────────────
const CLOUDINARY_CLOUD_NAME = "decikvy0b";
const CLOUDINARY_UPLOAD_PRESET = "ij5ifyyw";

// ─── Label Definitions ───────────────────────────────────────────────────────

const helpTypeLabels = {
  english: {
    selectHelpType: "What kind of help do you need?",
    medical: "Medical Help",
    education: "Education Help",
    ration: "Ration Help",
  },
  urdu: {
    selectHelpType: "آپ کو کس قسم کی مدد چاہیے؟",
    medical: "طبی مدد",
    education: "تعلیمی مدد",
    ration: "راشن مدد",
  },
  hindi: {
    selectHelpType: "आपको किस प्रकार की सहायता चाहिए?",
    medical: "चिकित्सा सहायता",
    education: "शिक्षा सहायता",
    ration: "राशन सहायता",
  },
};

// Beneficiary name labels per help type and language
const beneficiaryLabels = {
  english: {
    medical: "Patient Name",
    education: "Student Name",
    ration: "Recipient Name",
  },
  urdu: {
    medical: "مریض کا نام",
    education: "طالب علم کا نام",
    ration: "وصول کنندہ کا نام",
  },
  hindi: {
    medical: "मरीज़ का नाम",
    education: "छात्र का नाम",
    ration: "प्राप्तकर्ता का नाम",
  },
};

const docLabels = {
  english: {
    uploadTitle: {
      medical: "Upload Documents for Medical Help (Optional)",
      education: "Upload Documents for Education Help (Optional)",
      ration: "Upload Documents for Ration Help (Optional)",
    },
    beneficiaryName: {
      medical: "Patient Name",
      education: "Student Name",
      ration: "Recipient Name",
    },
    required: "Required",
    optional: "Documents are optional. You may upload them to support your request.",
    proceed: "Proceed to Form",
    uploading: "Uploading...",
    docs: {
      medical: [
        { key: "aadharCard", label: "Aadhar Card" },
        { key: "rationCard", label: "Ration Card" },
        { key: "doctorPrescription", label: "Doctor Prescription" },
        { key: "medicalReports", label: "Medical Reports" },
        { key: "hospitalBills", label: "Hospital Bills" },
      ],
      education: [
        { key: "aadharCard", label: "Aadhar Card" },
        { key: "feesStructure", label: "Fees Structure / School Contract" },
        { key: "schoolIdCard", label: "School ID Card" },
        { key: "lastYearResult", label: "Last Year Result" },
      ],
      ration: [
        { key: "aadharCard", label: "Aadhar Card" },
        { key: "rationCard", label: "Ration Card" },
        { key: "houseAgreement", label: "House Agreement" },
        { key: "electricBill", label: "Electric Bill" },
      ],
    },
  },
  urdu: {
    uploadTitle: {
      medical: "طبی مدد کے لیے دستاویزات اپ لوڈ کریں (اختیاری)",
      education: "تعلیمی مدد کے لیے دستاویزات اپ لوڈ کریں (اختیاری)",
      ration: "راشن مدد کے لیے دستاویزات اپ لوڈ کریں (اختیاری)",
    },
    beneficiaryName: {
      medical: "مریض کا نام",
      education: "طالب علم کا نام",
      ration: "وصول کنندہ کا نام",
    },
    required: "ضروری",
    optional: "دستاویزات اختیاری ہیں۔ آپ انہیں اپنی درخواست کی حمایت کے لیے اپ لوڈ کر سکتے ہیں۔",
    proceed: "فارم پر جائیں",
    uploading: "اپ لوڈ ہو رہا ہے...",
    docs: {
      medical: [
        { key: "aadharCard", label: "آدھار کارڈ" },
        { key: "rationCard", label: "راشن کارڈ" },
        { key: "doctorPrescription", label: "ڈاکٹر کا نسخہ" },
        { key: "medicalReports", label: "طبی رپورٹیں" },
        { key: "hospitalBills", label: "ہسپتال کے بل" },
      ],
      education: [
        { key: "aadharCard", label: "آدھار کارڈ" },
        { key: "feesStructure", label: "فیس کا ڈھانچہ / اسکول معاہدہ" },
        { key: "schoolIdCard", label: "اسکول آئی ڈی کارڈ" },
        { key: "lastYearResult", label: "گزشتہ سال کا نتیجہ" },
      ],
      ration: [
        { key: "aadharCard", label: "آدھار کارڈ" },
        { key: "rationCard", label: "راشن کارڈ" },
        { key: "houseAgreement", label: "مکان کا معاہدہ" },
        { key: "electricBill", label: "بجلی کا بل" },
      ],
    },
  },
  hindi: {
    uploadTitle: {
      medical: "चिकित्सा सहायता के लिए दस्तावेज़ अपलोड करें (वैकल्पिक)",
      education: "शिक्षा सहायता के लिए दस्तावेज़ अपलोड करें (वैकल्पिक)",
      ration: "राशन सहायता के लिए दस्तावेज़ अपलोड करें (वैकल्पिक)",
    },
    beneficiaryName: {
      medical: "मरीज़ का नाम",
      education: "छात्र का नाम",
      ration: "प्राप्तकर्ता का नाम",
    },
    required: "आवश्यक",
    optional: "दस्तावेज़ वैकल्पिक हैं। आप उन्हें अपने अनुरोध के समर्थन के लिए अपलोड कर सकते हैं।",
    proceed: "फ़ॉर्म पर जाएं",
    uploading: "अपलोड हो रहा है...",
    docs: {
      medical: [
        { key: "aadharCard", label: "आधार कार्ड" },
        { key: "rationCard", label: "राशन कार्ड" },
        { key: "doctorPrescription", label: "डॉक्टर का पर्चा" },
        { key: "medicalReports", label: "मेडिकल रिपोर्ट" },
        { key: "hospitalBills", label: "अस्पताल के बिल" },
      ],
      education: [
        { key: "aadharCard", label: "आधार कार्ड" },
        { key: "feesStructure", label: "फीस संरचना / स्कूल अनुबंध" },
        { key: "schoolIdCard", label: "स्कूल आईडी कार्ड" },
        { key: "lastYearResult", label: "पिछले साल का परिणाम" },
      ],
      ration: [
        { key: "aadharCard", label: "आधार कार्ड" },
        { key: "rationCard", label: "राशन कार्ड" },
        { key: "houseAgreement", label: "मकान समझौता" },
        { key: "electricBill", label: "बिजली का बिल" },
      ],
    },
  },
};

const formLabels = {
  english: {
    fullName: "Full Name",
    phoneNumber: "Phone Number",
    address: "Address",
    error_toast: "Please fill in the details correctly",
    totalFamilyMembers: "Total Family Members",
    familyMembers: "List of Family Members (Name - Age - Relation)",
    qualification: "Qualification",
    familyMonthlyIncome: "Family Monthly Income",
    occupation: "Occupation",
    rentStatus: "Rent Status",
    otherHelp: {
      medical: "Did you get any kind of medical help from anyone previously?",
      education: "Did you get any kind of educational help from anyone previously?",
      ration: "Did you get any kind of ration help from anyone previously?",
    },
    otherHelpDetails: {
      medical: "When and who helped?",
      education: "When and who helped?",
      ration: "When and who helped?",
    },
    submitButton: "Submit",
    submitting: "Submitting...",
    changeLanguage: "Change Language",
    formIntro:
      "For any request or need, please help us complete your verification by filling out the following:",
    select: "Select",
    rented: "Rented",
    owned: "Owned",
    shared: "Shared",
    yes: "Yes",
    no: "No",
    age: "Age",
    years: "Years",
    months: "Months",
    relation: "Relation",
  },
  urdu: {
    fullName: "مکمل نام",
    phoneNumber: "فون نمبر",
    address: "پتہ",
    error_toast: "براہِ مہربانی تفصیلات صحیح طریقے سے پُر کریں۔",
    totalFamilyMembers: "خاندان کے کل افراد",
    familyMembers: "خاندان کے افراد کی فہرست (نام - عمر - رشتہ)",
    qualification: "تعلیم",
    familyMonthlyIncome: "خاندان کی ماہانہ آمدنی",
    occupation: "پیشہ",
    rentStatus: "کرایہ کی صورتحال",
    otherHelp: {
      medical: "کیا آپ کو پہلے کسی سے طبی مدد ملی؟",
      education: "کیا آپ کو پہلے کسی سے تعلیمی مدد ملی؟",
      ration: "کیا آپ کو پہلے کسی سے راشن میں مدد ملی؟",
    },
    otherHelpDetails: {
      medical: "کب اور کس نے مدد کی؟",
      education: "کب اور کس نے مدد کی؟",
      ration: "کب اور کس نے مدد کی؟",
    },
    submitButton: "جمع کرائیں",
    submitting: "جمع ہو رہا ہے...",
    changeLanguage: "زبان تبدیل کریں",
    formIntro:
      "کسی بھی درخواست یا ضرورت کے لیے، براہ کرم درج ذیل کو پُر کر کے اپنی تصدیق مکمل کرنے میں ہماری مدد کریں:",
    select: "منتخب کریں",
    rented: "کرایہ پر",
    owned: "ملکیت",
    shared: "مشترکہ",
    yes: "ہاں",
    no: "نہیں",
    age: "عمر",
    years: "سال",
    months: "مہینہ",
    relation: "رشتہ",
  },
  hindi: {
    fullName: "पूरा नाम",
    phoneNumber: "फोन नंबर",
    address: "पता",
    error_toast: "कृपया विवरण सही से भरें",
    totalFamilyMembers: "कुल परिवार के सदस्य",
    familyMembers: "परिवार के सदस्यों की सूची (नाम - आयु - रिश्ता)",
    qualification: "योग्यता",
    familyMonthlyIncome: "परिवार की मासिक आय",
    occupation: "पेशा",
    rentStatus: "किराए की स्थिति",
    otherHelp: {
      medical: "क्या आपको पहले किसी से चिकित्सा सहायता मिली थी?",
      education: "क्या आपको पहले किसी से शैक्षिक सहायता मिली थी?",
      ration: "क्या आपको पहले किसी से राशन में सहायता मिली थी?",
    },
    otherHelpDetails: {
      medical: "कब और किसने मदद की?",
      education: "कब और किसने मदद की?",
      ration: "कब और किसने मदद की?",
    },
    submitButton: "जमा करें",
    submitting: "जमा हो रहा है...",
    changeLanguage: "भाषा बदलें",
    formIntro:
      "किसी भी अनुरोध या आवश्यकता के लिए, कृपया निम्नलिखित को भरकर अपनी सत्यापन पूरा करने में हमारी सहायता करें:",
    select: "चुनें",
    rented: "किराए पर",
    owned: "स्वामित्व",
    shared: "साझा",
    yes: "हाँ",
    no: "नहीं",
    age: "उम्र",
    years: "साल",
    months: "महीना",
    relation: "रिश्ता",
  },
};

const ACCEPTED_TYPES =
  "image/png,image/jpg,image/jpeg,image/webp,image/tiff,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// ─── Main Component ───────────────────────────────────────────────────────────
const NeedHelp = () => {
  const currentTheme = useSelector((state) => state.theme.current);

  const [step, setStep] = useState("language");
  const [language, setLanguage] = useState(null);
  const [helpType, setHelpType] = useState(null);

  // Beneficiary name (patient / student / recipient)
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [docFiles, setDocFiles] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState({});

  const initialFormData = {
    fullName: "",
    phoneNumber: "",
    address: "",
    totalFamilyMembers: "",
    familyMembers: [],
    qualification: "",
    familyMonthlyIncome: "",
    occupation: "",
    rentStatus: "",
    otherHelp: "",
    otherHelpDetails: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const regex = {
    fullName: /^[\p{L}\s]{2,500}$/u,
    phoneNumber: /^\+?[\d\s-]{10}$/,
    address: /^.{5,10000}$/,
    totalFamilyMembers: /^([0-9]|1[0-9]|20)$/,
    name: /^[\p{L}\s]{2,500}$/u,
    age: /^[1-9]\d?$/,
    relation: /^[\p{L}0-9\s]{2,200}$/u,
    qualification: /^.{2,1000}$/,
    familyMonthlyIncome: /^\d{1,10}$/,
    occupation: /^.{2,500}$/,
    rentStatus: /^(rented|owned|shared)$/,
    otherHelp: /^(Yes|No)$/,
    otherHelpDetails: /^.{0,20000}$/,
  };

  useEffect(() => {
    const count = formData.totalFamilyMembers
      ? parseInt(formData.totalFamilyMembers, 10)
      : 0;
    if (isNaN(count) || count < 0) {
      setFormData((prev) => ({ ...prev, familyMembers: [] }));
      return;
    }
    const currentCount = formData.familyMembers.length;
    if (count > currentCount) {
      setFormData((prev) => ({
        ...prev,
        familyMembers: [
          ...prev.familyMembers,
          ...Array(count - currentCount)
            .fill()
            .map(() => ({ name: "", age: "", years: "years", relation: "" })),
        ],
      }));
    } else if (count < currentCount) {
      setFormData((prev) => ({
        ...prev,
        familyMembers: prev.familyMembers.slice(0, count),
      }));
    }
  }, [formData.totalFamilyMembers]);

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    if (name === "totalFamilyMembers") {
      const sanitizedValue = value === "" ? "" : value < 0 ? "0" : value;
      setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    } else if (name.startsWith("familyMembers")) {
      const updatedFamilyMembers = [...formData.familyMembers];
      const [, field] = name.split(".");
      updatedFamilyMembers[index][field] = value;
      setFormData((prev) => ({ ...prev, familyMembers: updatedFamilyMembers }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDocFileChange = (key, file) => {
    setDocFiles((prev) => ({ ...prev, [key]: file }));
  };

  // ── Document Upload to Cloudinary (OPTIONAL) ──
  const uploadDocuments = async () => {
    const dl = docLabels[language];

    // Validate beneficiary name (required for all help types)
    if (!beneficiaryName.trim()) {
      toast.error(
        `${dl.beneficiaryName[helpType]} is required`,
        { position: "top-center", autoClose: 3000 }
      );
      return false;
    }

    // Only upload files that were actually selected
    const filesToUpload = Object.entries(docFiles).filter(([, file]) => file);

    if (filesToUpload.length === 0) {
      // No files selected — skip upload entirely
      setUploadedUrls({});
      return true;
    }

    setIsUploading(true);
    try {
      const timestamp = Date.now();
      const urls = {};

      for (const [key, file] of filesToUpload) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
        fd.append(
          "public_id",
          `helpRequests/${helpType}/${timestamp}/${key}`
        );
        const ext = file.name.split(".").pop().toLowerCase();
        const isRaw = ["pdf", "doc", "docx", "tiff"].includes(ext);
        const resourceType = isRaw ? "raw" : "image";

        const res = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`,
          { method: "POST", body: fd }
        );

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData?.error?.message || "Upload failed");
        }

        const data = await res.json();
        urls[key] = {
          url: data.secure_url,
          name: file.name,
          key,
          resourceType,
          publicId: data.public_id,
        };
      }

      setUploadedUrls(urls);
      setIsUploading(false);
      return true;
    } catch (err) {
      console.error("Cloudinary upload error:", err);
      toast.error(`File upload failed: ${err.message}`, {
        position: "top-center",
        autoClose: 4000,
      });
      setIsUploading(false);
      return false;
    }
  };

  const handleDocumentsProceed = async () => {
    const success = await uploadDocuments();
    if (success) setStep("form");
  };

  const validateForm = () => {
    const labels = formLabels[language];
    const newErrors = {};

    if (!regex.fullName.test(formData.fullName))
      newErrors.fullName = labels.fullName + " (2-50 letters)";
    if (!regex.phoneNumber.test(formData.phoneNumber))
      newErrors.phoneNumber = labels.phoneNumber + " (10 digits)";
    if (!regex.address.test(formData.address))
      newErrors.address = labels.address + " (5-100 characters)";
    const totalMembers = parseInt(formData.totalFamilyMembers, 10);
    if (isNaN(totalMembers) || totalMembers < 0 || totalMembers > 20)
      newErrors.totalFamilyMembers = labels.totalFamilyMembers + " (0-20)";
    if (!regex.qualification.test(formData.qualification))
      newErrors.qualification = labels.qualification + " (2-100 characters)";
    if (!regex.familyMonthlyIncome.test(formData.familyMonthlyIncome))
      newErrors.familyMonthlyIncome =
        labels.familyMonthlyIncome + " (positive numbers only)";
    if (!regex.occupation.test(formData.occupation))
      newErrors.occupation = labels.occupation + " (2-50 characters)";
    if (!regex.rentStatus.test(formData.rentStatus))
      newErrors.rentStatus = labels.rentStatus + " (select an option)";
    if (!regex.otherHelp.test(formData.otherHelp))
      newErrors.otherHelp = labels.otherHelp[helpType] + " (select an option)";
    if (
      formData.otherHelp === "Yes" &&
      !regex.otherHelpDetails.test(formData.otherHelpDetails)
    )
      newErrors.otherHelpDetails =
        labels.otherHelpDetails[helpType] + " (up to 200 characters)";

    formData.familyMembers.forEach((member, index) => {
      if (!regex.name.test(member.name))
        newErrors[`familyMembers[${index}].name`] =
          labels.familyMembers + " - Name (2-50 letters)";
      if (!regex.age.test(member.age))
        newErrors[`familyMembers[${index}].age`] =
          labels.familyMembers + " - Age (1-99)";
      if (!regex.relation.test(member.relation))
        newErrors[`familyMembers[${index}].relation`] =
          labels.familyMembers + " - Relation (2-20 letters)";
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const errorMessage = newErrors[firstErrorKey];
      toast.error(
        Object.keys(newErrors).length === 1 ? errorMessage : labels.error_toast,
        { position: "top-center", autoClose: 3000 }
      );
      const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
      if (errorElement)
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const labels = formLabels[language];
    const bLabel = beneficiaryLabels[language][helpType];

    const docSummary =
      Object.keys(uploadedUrls).length > 0
        ? Object.entries(uploadedUrls)
            .map(([key, val]) => `${key}: ${val.url}`)
            .join("\n")
        : "No documents uploaded";

    const htmlBody = `
HELP TYPE: ${helpType.toUpperCase()}
LANGUAGE: ${language}
${bLabel}: ${beneficiaryName}
${labels.fullName}: ${formData.fullName}
${labels.phoneNumber}: ${formData.phoneNumber}
${labels.address}: ${formData.address}
${labels.totalFamilyMembers}: ${formData.totalFamilyMembers || 0}
${labels.familyMembers}:
${formData.familyMembers
  .map(
    (m, i) =>
      `  ${i + 1}. Name: ${m.name}, Age: ${m.age} ${m.years}, Relation: ${m.relation}`
  )
  .join("\n")}
${labels.qualification}: ${formData.qualification}
${labels.familyMonthlyIncome}: ${formData.familyMonthlyIncome}
${labels.occupation}: ${formData.occupation}
${labels.rentStatus}: ${formData.rentStatus}
${labels.otherHelp[helpType]}: ${formData.otherHelp}
${formData.otherHelp === "Yes" ? `${labels.otherHelpDetails[helpType]}: ${formData.otherHelpDetails}` : ""}

UPLOADED DOCUMENTS:
${docSummary}
    `.trim();

    try {
      // Save ONLY to HelpRequests collection
      await addDoc(collection(db, "HelpRequests"), {
        date: Timestamp.now(),
        helpType,
        language,
        status: "pending",
        beneficiaryName,
        beneficiaryLabel: bLabel,
        formData: {
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          totalFamilyMembers: formData.totalFamilyMembers,
          familyMembers: formData.familyMembers,
          qualification: formData.qualification,
          familyMonthlyIncome: formData.familyMonthlyIncome,
          occupation: formData.occupation,
          rentStatus: formData.rentStatus,
          otherHelp: formData.otherHelp,
          otherHelpDetails: formData.otherHelpDetails,
        },
        documents: uploadedUrls,
        details: htmlBody,
        bookedBy: "",
      });
    } catch (err) {
      console.error("Firestore error:", err);
    }

    // Discord webhook
    try {
      await fetch(
        "https://discord.com/api/webhooks/1473257568242499747/njEGSu9Hd0_uuWTHsbC059UXIp5R80zjNf-HrXINvs70Ci1EFG3pha-t4AQTjyFncmlA",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            embeds: [
              {
                title: `New ${helpType} Help Form Submission`,
                color: 5814783,
                description: htmlBody.slice(0, 4000),
                timestamp: new Date(),
              },
            ],
          }),
        }
      );
    } catch (err) {
      console.error("Discord webhook error:", err);
    }

    const emailData = {
      to_email: "alqaimwelfareinfo@gmail.com",
      from_name: "Al Qaim Welfare Foundation Form",
      subject: `${helpType.toUpperCase()} - ${formData.fullName} - ${formData.phoneNumber}`,
      message: htmlBody,
    };

    emailjs
      .send(
        "service_y5g4vjs",
        "template_1ayop5o",
        emailData,
        "ivuz1YIJqTWXipbyc"
      )
      .then(() => {
        toast.success("Form submitted successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
        setFormData(initialFormData);
        setErrors({});
        setDocFiles({});
        setUploadedUrls({});
        setBeneficiaryName("");
        setIsSubmitting(false);
        setTimeout(() => {
          window.location.href = "/needhelp";
        }, 3500);
      })
      .catch((error) => {
        toast.error("Failed to send email. Please try again.", {
          position: "top-center",
          autoClose: 3000,
        });
        console.error("EmailJS error:", error);
        setIsSubmitting(false);
      });
  };

  // ─── STEP: Language Selection ─────────────────────────────────────────────
  if (step === "language") {
    return (
      <>
        <Navbar />
        <div className="nh-selection-screen">
          <div className="nh-selection-card">
            <div className="nh-logo-mark">﷽</div>
            <h1 className="nh-selection-title">Select Language</h1>
            <p className="nh-selection-sub">زبان منتخب کریں · भाषा चुनें</p>
            <div className="nh-selection-buttons">
              {["english", "urdu", "hindi"].map((lang) => (
                <button
                  key={lang}
                  className="nh-sel-btn"
                  style={{ background: currentTheme }}
                  onClick={() => {
                    setLanguage(lang);
                    setStep("helpType");
                  }}
                >
                  {lang === "english"
                    ? "English"
                    : lang === "urdu"
                    ? "اردو"
                    : "हिंदी"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ToastContainer />
        <Footer />
      </>
    );
  }

  // ─── STEP: Help Type Selection ────────────────────────────────────────────
  if (step === "helpType") {
    const ht = helpTypeLabels[language];
    return (
      <>
        <Navbar />
        <div className="nh-selection-screen">
          <div className="nh-selection-card">
            <div className="nh-logo-mark">﷽</div>
            <h1 className="nh-selection-title">{ht.selectHelpType}</h1>
            <div className="nh-selection-buttons">
              {["medical", "education", "ration"].map((type) => (
                <button
                  key={type}
                  className="nh-sel-btn nh-help-btn"
                  style={{ background: currentTheme }}
                  onClick={() => {
                    setHelpType(type);
                    setStep("documents");
                  }}
                >
                  <span className="nh-help-icon">
                    {type === "medical"
                      ? "🏥"
                      : type === "education"
                      ? "🎓"
                      : "🌾"}
                  </span>
                  {ht[type]}
                </button>
              ))}
            </div>
            <button
              className="nh-back-link"
              onClick={() => setStep("language")}
            >
              ← Back
            </button>
          </div>
        </div>
        <ToastContainer />
        <Footer />
      </>
    );
  }

  // ─── STEP: Document Upload (Optional) ────────────────────────────────────
  if (step === "documents") {
    const dl = docLabels[language];
    const requiredDocs = dl.docs[helpType];
    const isRTL = language === "urdu";
    const bLabel = dl.beneficiaryName[helpType];

    return (
      <>
        <Navbar />
        <div className="nh-doc-screen" dir={isRTL ? "rtl" : "ltr"}>
          <div className="nh-doc-card">
            <div className="nh-doc-badge">
              {helpType === "medical"
                ? "🏥"
                : helpType === "education"
                ? "🎓"
                : "🌾"}
            </div>
            <h2 className="nh-doc-title">{dl.uploadTitle[helpType]}</h2>
            <p className="nh-doc-optional-note">{dl.optional}</p>

            {/* Beneficiary name (required) */}
            <div className="nh-doc-group">
              <label className="nh-doc-label">
                {bLabel} <span className="nh-required-star">*</span>
              </label>
              <input
                type="text"
                className="nh-patient-input"
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder={bLabel}
              />
            </div>

            <div className="nh-doc-list">
              {requiredDocs.map(({ key, label }, idx) => (
                <div
                  key={key}
                  className={`nh-doc-group ${docFiles[key] ? "nh-doc-done" : ""}`}
                >
                  <label className="nh-doc-label">
                    <span className="nh-doc-num">{idx + 1}</span>
                    {label}
                    <span className="nh-optional-tag">optional</span>
                  </label>
                  <div className="nh-file-wrapper">
                    <input
                      type="file"
                      id={`doc-${key}`}
                      accept={ACCEPTED_TYPES}
                      className="nh-file-input"
                      onChange={(e) =>
                        handleDocFileChange(key, e.target.files[0])
                      }
                    />
                    <label htmlFor={`doc-${key}`} className="nh-file-label">
                      {docFiles[key] ? (
                        <>
                          <span className="nh-file-check">✓</span>
                          <span className="nh-file-name">
                            {docFiles[key].name}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="nh-upload-icon">📎</span>
                          <span>Choose File</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="nh-doc-actions">
              <button
                className="nh-back-link"
                onClick={() => setStep("helpType")}
                disabled={isUploading}
              >
                ← Back
              </button>
              <button
                className="nh-proceed-btn"
                style={{ background: currentTheme }}
                onClick={handleDocumentsProceed}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <span className="nh-spinner"></span>
                    {dl.uploading}
                  </>
                ) : (
                  dl.proceed
                )}
              </button>
            </div>
          </div>
        </div>
        <ToastContainer />
        <Footer />
      </>
    );
  }

  // ─── STEP: Form ───────────────────────────────────────────────────────────
  const labels = formLabels[language];
  const isRTL = language === "urdu";

  return (
    <>
      <Navbar />
      <PageHeader title={helpTypeLabels[language][helpType]} />
      <div
        className="form-container"
        style={{ background: currentTheme }}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="language-change">
          <label>{labels.changeLanguage}</label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="english">English</option>
            <option value="urdu">Urdu</option>
            <option value="hindi">Hindi</option>
          </select>
        </div>

        <h1 className="form-title">
          Al-Qaim Welfare Foundation Verification Form – Follow-up
        </h1>
        <p className="form-intro">{labels.formIntro}</p>

        <div className="needhelp-form">
          {/* Full Name */}
          <div className="form-group">
            <label>{labels.fullName}</label>
            <input
              type="text"
              name="fullName"
              placeholder={labels.fullName}
              value={formData.fullName}
              onChange={handleChange}
              className={errors.fullName ? "error" : ""}
            />
            {errors.fullName && (
              <span className="error-message">{errors.fullName}</span>
            )}
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label>{labels.phoneNumber}</label>
            <input
              type="text"
              name="phoneNumber"
              placeholder={labels.phoneNumber}
              value={formData.phoneNumber}
              onChange={handleChange}
              className={errors.phoneNumber ? "error" : ""}
            />
            {errors.phoneNumber && (
              <span className="error-message">{errors.phoneNumber}</span>
            )}
          </div>

          {/* Address */}
          <div className="form-group">
            <label>{labels.address}</label>
            <textarea
              name="address"
              placeholder={labels.address}
              value={formData.address}
              onChange={handleChange}
              className={errors.address ? "error" : ""}
            />
            {errors.address && (
              <span className="error-message">{errors.address}</span>
            )}
          </div>

          {/* Total Family Members */}
          <div className="form-group">
            <label>{labels.totalFamilyMembers}</label>
            <input
              type="number"
              name="totalFamilyMembers"
              placeholder="0"
              value={formData.totalFamilyMembers}
              onChange={(e) => {
                let val = e.target.value;
                if (val === "") {
                  handleChange(e);
                  return;
                }
                if (Number(val) >= 0 && Number(val) <= 30) handleChange(e);
              }}
              className={errors.totalFamilyMembers ? "error" : ""}
              min="0"
              max="30"
            />
            {errors.totalFamilyMembers && (
              <span className="error-message">{errors.totalFamilyMembers}</span>
            )}
          </div>

          {/* Family Members */}
          <div className="form-group">
            <label>{labels.familyMembers}</label>
            {formData.familyMembers.map((member, index) => (
              <div key={index} className="family-member">
                <input
                  type="text"
                  name={`familyMembers[${index}].name`}
                  placeholder={labels.fullName}
                  value={member.name}
                  onChange={(e) => handleChange(e, index)}
                  className={
                    errors[`familyMembers[${index}].name`] ? "error" : ""
                  }
                />
                <input
                  type="number"
                  name={`familyMembers[${index}].age`}
                  placeholder={labels.age}
                  value={member.age}
                  onChange={(e) => handleChange(e, index)}
                  className={
                    errors[`familyMembers[${index}].age`] ? "error" : ""
                  }
                />
                <select
                  name={`familyMembers[${index}].years`}
                  onChange={(e) => handleChange(e, index)}
                  value={member.years}
                >
                  <option value="years">{labels.years}</option>
                  <option value="months">{labels.months}</option>
                </select>
                <input
                  type="text"
                  name={`familyMembers[${index}].relation`}
                  placeholder={labels.relation}
                  value={member.relation}
                  onChange={(e) => handleChange(e, index)}
                  className={
                    errors[`familyMembers[${index}].relation`] ? "error" : ""
                  }
                />
                {errors[`familyMembers[${index}].name`] && (
                  <span className="error-message">
                    {errors[`familyMembers[${index}].name`]}
                  </span>
                )}
                {errors[`familyMembers[${index}].age`] && (
                  <span className="error-message">
                    {errors[`familyMembers[${index}].age`]}
                  </span>
                )}
                {errors[`familyMembers[${index}].relation`] && (
                  <span className="error-message">
                    {errors[`familyMembers[${index}].relation`]}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Qualification */}
          <div className="form-group">
            <label>{labels.qualification}</label>
            <input
              type="text"
              name="qualification"
              placeholder={labels.qualification}
              value={formData.qualification}
              onChange={handleChange}
              className={errors.qualification ? "error" : ""}
            />
            {errors.qualification && (
              <span className="error-message">{errors.qualification}</span>
            )}
          </div>

          {/* Family Monthly Income */}
          <div className="form-group">
            <label>{labels.familyMonthlyIncome}</label>
            <input
              type="number"
              name="familyMonthlyIncome"
              placeholder="0"
              value={formData.familyMonthlyIncome}
              onChange={handleChange}
              className={errors.familyMonthlyIncome ? "error" : ""}
            />
            {errors.familyMonthlyIncome && (
              <span className="error-message">
                {errors.familyMonthlyIncome}
              </span>
            )}
          </div>

          {/* Occupation */}
          <div className="form-group">
            <label>{labels.occupation}</label>
            <input
              type="text"
              name="occupation"
              placeholder={labels.occupation}
              value={formData.occupation}
              onChange={handleChange}
              className={errors.occupation ? "error" : ""}
            />
            {errors.occupation && (
              <span className="error-message">{errors.occupation}</span>
            )}
          </div>

          {/* Rent Status */}
          <div className="form-group">
            <label>{labels.rentStatus}</label>
            <select
              name="rentStatus"
              value={formData.rentStatus}
              onChange={handleChange}
              className={errors.rentStatus ? "error" : ""}
            >
              <option value="">{labels.select}</option>
              <option value="rented">{labels.rented}</option>
              <option value="owned">{labels.owned}</option>
              <option value="shared">{labels.shared}</option>
            </select>
            {errors.rentStatus && (
              <span className="error-message">{errors.rentStatus}</span>
            )}
          </div>

          {/* Other Help */}
          <div className="form-group">
            <label>{labels.otherHelp[helpType]}</label>
            <select
              name="otherHelp"
              value={formData.otherHelp}
              onChange={handleChange}
              className={errors.otherHelp ? "error" : ""}
            >
              <option value="">{labels.select}</option>
              <option value="Yes">{labels.yes}</option>
              <option value="No">{labels.no}</option>
            </select>
            {errors.otherHelp && (
              <span className="error-message">{errors.otherHelp}</span>
            )}
          </div>

          {formData.otherHelp === "Yes" && (
            <div className="form-group">
              <label>{labels.otherHelpDetails[helpType]}</label>
              <textarea
                name="otherHelpDetails"
                placeholder={labels.otherHelpDetails[helpType]}
                value={formData.otherHelpDetails}
                onChange={handleChange}
                className={errors.otherHelpDetails ? "error" : ""}
              />
              {errors.otherHelpDetails && (
                <span className="error-message">{errors.otherHelpDetails}</span>
              )}
            </div>
          )}

          <button
            onClick={handleSubmit}
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                {labels.submitting}
              </>
            ) : (
              labels.submitButton
            )}
          </button>
        </div>
      </div>
      <ToastContainer />
      <Footer />
    </>
  );
};

export default NeedHelp;